import CustomFileSystemProvider from 'devextreme/file_management/custom_provider';
import FileSystemError from 'devextreme/file_management/error';
import type FileSystemItem from 'devextreme/file_management/file_system_item';
import type UploadInfo from 'devextreme/file_management/upload_info';
import { apiFetch, readErrorMessage } from '../api/client';
import { deleteInfoUrl, downloadFileUrl, listItems, mountApiBase, type MountSummary } from './fileBrowserApi';
import { buildDownloadZipRequest, downloadItemsAsZip } from './fileBrowserDownload';
import { resetTransferBatchState, type OnConflictPolicy } from './fileConflictDialog';
import { performFileTransfer } from './fileBrowserTransfer';
import { isMountUnavailable, mountHealthMessage } from './fileBrowserMountAccess';

/** DevExtreme chunk uploads call uploadFileChunk once per chunk; our API accepts whole files only. */
type UploadInfoWithDeferred = UploadInfo & {
  customData: UploadInfo['customData'] & { deferred?: Promise<void> };
};

const inFlightUploads = new Map<string, Promise<void>>();

function uploadDedupeKey(mountId: string, relativePath: string, fileName: string): string {
  return `${mountId}|${relativePath}|${fileName}`;
}

const FILE_MANAGER_ERROR = {
  NoAccess: 0,
  FileExists: 1,
  FileNotFound: 2,
  DirectoryExists: 3,
  DirectoryNotFound: 4,
  Other: 32767
} as const;

export interface BrowseItemRaw {
  name: string;
  path: string;
  isDirectory: boolean;
  hasSubDirectories: boolean;
  size: number | null;
  dateModified: string | null;
  dateCreated: string | null;
}

/** Mount enriched with the unique display key used as its virtual root folder name. */
export interface MountRoute extends MountSummary {
  key: string;
}

export interface ParsedVirtualPath {
  mount: MountRoute;
  relative: string;
}

export interface DeleteInfo {
  name: string;
  isDirectory: boolean;
  isEmpty: boolean;
  directChildCount: number;
}

export interface TransferRequest {
  sourceMountId: string;
  sourcePath: string;
  destinationMountId: string;
  destinationPath: string;
  operation: 'Copy' | 'Move';
  onConflict?: OnConflictPolicy;
}

export function buildMountRoutes(mounts: readonly MountSummary[]): MountRoute[] {
  const used = new Set<string>();
  const routes: MountRoute[] = [];
  for (const mount of mounts) {
    const base = (mount.displayName || 'Share').trim() || 'Share';
    let key = base;
    let suffix = 2;
    while (used.has(key)) {
      key = `${base} (${suffix})`;
      suffix += 1;
    }
    used.add(key);
    routes.push({ ...mount, key });
  }
  return routes;
}

export function parseVirtualPath(
  virtualPath: string | null | undefined,
  routes: readonly MountRoute[]
): ParsedVirtualPath | null {
  const trimmed = (virtualPath ?? '').replace(/\\/g, '/').replace(/^\/+/, '');
  if (trimmed.length === 0) return null;

  const slash = trimmed.indexOf('/');
  const head = slash === -1 ? trimmed : trimmed.substring(0, slash);
  const rest = slash === -1 ? '' : trimmed.substring(slash + 1);

  const mount = routes.find((route) => route.key === head);
  if (!mount) return null;
  return { mount, relative: rest };
}

export function toVirtualPath(mountKey: string, relative: string): string {
  const rel = (relative ?? '').replace(/^\/+/, '');
  return rel.length === 0 ? mountKey : `${mountKey}/${rel}`;
}

export function toFileManagerItem(mountKey: string, item: BrowseItemRaw) {
  const virtualItemPath = toVirtualPath(mountKey, item.path);
  return {
    key: virtualItemPath,
    name: item.name,
    path: virtualItemPath,
    isDirectory: item.isDirectory,
    // Honor the server flag so childless folders lose the tree chevron. Lazy mounts
    // (OneDrive) report every folder as expandable, keeping folder restore working.
    hasSubDirectories: item.isDirectory && item.hasSubDirectories,
    size: item.size ?? 0,
    dateModified: item.dateModified ? new Date(item.dateModified) : undefined,
    __path: virtualItemPath
  };
}

export function parseItemPathForRoutes(
  item: { path?: string; key?: string; name: string; dataItem?: unknown },
  routes: readonly MountRoute[]
): ParsedVirtualPath | null {
  return parseVirtualPath(getItemPath(item), routes);
}

export async function getDeleteInfo(
  item: { path?: string; key?: string; name: string; dataItem?: unknown },
  routes: readonly MountRoute[]
): Promise<DeleteInfo> {
  const parsed = parseItemPathForRoutes(item, routes);
  if (!parsed || !parsed.relative) {
    throw new Error('Storage locations cannot be deleted from the file browser.');
  }
  assertMountAvailable(parsed.mount);

  const res = await apiFetch(deleteInfoUrl(parsed.mount.id, parsed.relative));
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }

  return res.json() as Promise<DeleteInfo>;
}

export function buildTransferRequest(
  item: { path?: string; key?: string; name: string; dataItem?: unknown },
  destinationDirectory: { path?: string; key?: string; name: string; dataItem?: unknown },
  routes: readonly MountRoute[],
  operation: 'Copy' | 'Move'
): TransferRequest {
  const source = parseItemPathForRoutes(item, routes);
  if (!source || !source.relative) {
    throw new Error('Storage locations cannot be copied or moved.');
  }

  const destination = parseItemPathForRoutes(destinationDirectory, routes);
  if (!destination) {
    throw new Error('Choose a storage location or folder as the destination.');
  }

  if (destination.mount.mode !== 'ReadWrite') {
    throw new Error(`"${destination.mount.key}" is read-only.`);
  }
  assertMountAvailable(source.mount);
  assertMountAvailable(destination.mount);

  if (operation === 'Move' && source.mount.mode !== 'ReadWrite') {
    throw new Error(`"${source.mount.key}" is read-only.`);
  }

  return {
    sourceMountId: source.mount.id,
    sourcePath: source.relative,
    destinationMountId: destination.mount.id,
    destinationPath: destination.relative,
    operation
  };
}

/** Presents each storage mount as a top-level directory in DevExtreme FileManager. */
export function createUnifiedFileBrowserProvider(mounts: readonly MountSummary[]): CustomFileSystemProvider {
  const routes = buildMountRoutes(mounts);
  resetTransferBatchState();

  return new CustomFileSystemProvider({
    getItems(parentDirectory) {
      const virtualPath = ((parentDirectory as { path?: string })?.path ?? '')
        .replace(/\\/g, '/')
        .replace(/^\/+/, '');

      if (virtualPath.length === 0) {
        return Promise.all(
          routes.map(async (mount) => ({
            key: mount.key,
            name: mount.key,
            path: mount.key,
            isDirectory: true,
            // Always expandable when the share is available. Probing children here
            // is wrong for lazy mounts (OneDrive): an empty/slow first list makes
            // File Manager refuse to restore a saved folder under that share.
            hasSubDirectories: !isMountUnavailable(mount),
            size: 0,
            dateModified: undefined,
            __path: mount.key,
            __mountId: mount.id,
            __isMountRoot: true
          }))
        );
      }

      const parsed = parseVirtualPath(virtualPath, routes);
      if (!parsed) {
        return Promise.reject(
          fileManagerError(`Share "${virtualPath.split('/')[0]}" is no longer available.`, parentDirectory, FILE_MANAGER_ERROR.DirectoryNotFound)
        );
      }
      if (isMountUnavailable(parsed.mount)) {
        return Promise.reject(
          fileManagerError(mountHealthMessage(parsed.mount) ?? 'This storage location is unavailable.', parentDirectory, FILE_MANAGER_ERROR.NoAccess)
        );
      }

      return listItems(parsed.mount.id, parsed.relative)
        .then((items) =>
          items.map((item) => ({
            ...toFileManagerItem(parsed.mount.key, item),
            __mountId: parsed.mount.id
          }))
        )
        .catch((error: unknown) =>
          Promise.reject(
            fileManagerError(
              error instanceof Error ? error.message : 'The directory could not be opened.',
              parentDirectory,
              FILE_MANAGER_ERROR.Other
            )
          )
        );
    },

    createDirectory(parentDirectory, name) {
      const parsed = parseParentDirectory(parentDirectory, routes);
      if (!parsed) return Promise.reject(fileManagerError('Open a storage location first to create a folder.', parentDirectory, FILE_MANAGER_ERROR.NoAccess));
      try { assertMountAvailable(parsed.mount); } catch (error) {
        return Promise.reject(fileManagerError(error instanceof Error ? error.message : String(error), parentDirectory, FILE_MANAGER_ERROR.NoAccess));
      }
      if (parsed.mount.mode !== 'ReadWrite') {
        return Promise.reject(fileManagerError(`"${parsed.mount.key}" is read-only.`, parentDirectory, FILE_MANAGER_ERROR.NoAccess));
      }
      const targetPath = parsed.relative ? `${parsed.relative}/${name}` : name;
      return apiFetch(`${mountApiBase(parsed.mount.id)}/directories`, {
        method: 'POST',
        body: JSON.stringify({ path: targetPath })
      }).then((res) => checkOk(res, parentDirectory, FILE_MANAGER_ERROR.DirectoryExists));
    },

    deleteItem(item) {
      const parsed = parseVirtualPath(getItemPath(item), routes);
      if (!parsed) return Promise.reject(fileManagerError('This item cannot be deleted.', item, FILE_MANAGER_ERROR.FileNotFound));
      try { assertMountAvailable(parsed.mount); } catch (error) {
        return Promise.reject(fileManagerError(error instanceof Error ? error.message : String(error), item, FILE_MANAGER_ERROR.NoAccess));
      }
      if (parsed.mount.mode !== 'ReadWrite') {
        return Promise.reject(fileManagerError(`"${parsed.mount.key}" is read-only.`, item, FILE_MANAGER_ERROR.NoAccess));
      }
      if (!parsed.relative) {
        return Promise.reject(fileManagerError('Storage locations cannot be deleted from the file browser.', item, FILE_MANAGER_ERROR.NoAccess));
      }
      return apiFetch(`${mountApiBase(parsed.mount.id)}/items?path=${encodeURIComponent(parsed.relative)}`, {
        method: 'DELETE'
      }).then((res) =>
        checkOk(res, item, item.isDirectory ? FILE_MANAGER_ERROR.DirectoryNotFound : FILE_MANAGER_ERROR.FileNotFound)
      );
    },

    copyItem(item, destinationDirectory) {
      return transferItem(item, destinationDirectory, routes, 'Copy');
    },

    moveItem(item, destinationDirectory) {
      return transferItem(item, destinationDirectory, routes, 'Move');
    },

    renameItem(item, newName) {
      const parsed = parseVirtualPath(getItemPath(item), routes);
      if (!parsed) return Promise.reject(fileManagerError('This item cannot be renamed.', item, FILE_MANAGER_ERROR.FileNotFound));
      try { assertMountAvailable(parsed.mount); } catch (error) {
        return Promise.reject(fileManagerError(error instanceof Error ? error.message : String(error), item, FILE_MANAGER_ERROR.NoAccess));
      }
      if (parsed.mount.mode !== 'ReadWrite') {
        return Promise.reject(fileManagerError(`"${parsed.mount.key}" is read-only.`, item, FILE_MANAGER_ERROR.NoAccess));
      }
      if (!parsed.relative) {
        return Promise.reject(fileManagerError('Storage locations cannot be renamed from the file browser.', item, FILE_MANAGER_ERROR.NoAccess));
      }
      const fromPath = parsed.relative;
      const parent = fromPath.includes('/') ? fromPath.substring(0, fromPath.lastIndexOf('/')) : '';
      const toPath = parent ? `${parent}/${newName}` : newName;
      return apiFetch(`${mountApiBase(parsed.mount.id)}/rename`, {
        method: 'POST',
        body: JSON.stringify({ fromPath, toPath })
      }).then((res) =>
        checkOk(res, item, item.isDirectory ? FILE_MANAGER_ERROR.DirectoryExists : FILE_MANAGER_ERROR.FileExists)
      );
    },

    uploadFileChunk(fileData: File, uploadInfo: unknown, destinationDirectory: FileSystemItem) {
      const chunksInfo = uploadInfo as UploadInfoWithDeferred;
      if (!chunksInfo.customData) {
        chunksInfo.customData = {};
      }
      if (chunksInfo.chunkIndex > 0) {
        return chunksInfo.customData.deferred ?? Promise.resolve();
      }

      const parsed = parseParentDirectory(destinationDirectory, routes);
      if (!parsed) return Promise.reject(fileManagerError('Open a storage location first to upload files.', destinationDirectory, FILE_MANAGER_ERROR.NoAccess));
      try { assertMountAvailable(parsed.mount); } catch (error) {
        return Promise.reject(fileManagerError(error instanceof Error ? error.message : String(error), destinationDirectory, FILE_MANAGER_ERROR.NoAccess));
      }
      if (parsed.mount.mode !== 'ReadWrite') {
        return Promise.reject(fileManagerError(`"${parsed.mount.key}" is read-only.`, destinationDirectory, FILE_MANAGER_ERROR.NoAccess));
      }

      const dedupeKey = uploadDedupeKey(parsed.mount.id, parsed.relative, fileData.name);
      const inFlight = inFlightUploads.get(dedupeKey);
      if (inFlight) return inFlight;

      const form = new FormData();
      form.append('file', fileData);
      const uploadPromise = apiFetch(`${mountApiBase(parsed.mount.id)}/upload?path=${encodeURIComponent(parsed.relative)}`, {
        method: 'POST',
        body: form
      })
        .then((res) => checkOk(res, destinationDirectory, FILE_MANAGER_ERROR.FileExists))
        .finally(() => {
          inFlightUploads.delete(dedupeKey);
        });

      chunksInfo.customData.deferred = uploadPromise;
      inFlightUploads.set(dedupeKey, uploadPromise);
      return uploadPromise;
    },

    downloadItems(items) {
      if (!items.length) return Promise.resolve();

      if (items.length === 1) {
        const item = items[0]!;
        const parsed = parseVirtualPath(getItemPath(item), routes);
        if (!parsed) return Promise.reject(fileManagerError('This item cannot be downloaded.', item, FILE_MANAGER_ERROR.FileNotFound));
        try { assertMountAvailable(parsed.mount); } catch (error) {
          return Promise.reject(fileManagerError(error instanceof Error ? error.message : String(error), item, FILE_MANAGER_ERROR.NoAccess));
        }
        if (!parsed.relative) {
          return Promise.reject(fileManagerError('Storage locations cannot be downloaded as files.', item, FILE_MANAGER_ERROR.NoAccess));
        }
        window.open(downloadFileUrl(parsed.mount.id, parsed.relative), '_blank', 'noopener');
        return Promise.resolve();
      }

      let request;
      try {
        for (const item of items) {
          const parsed = parseVirtualPath(getItemPath(item), routes);
          if (parsed) assertMountAvailable(parsed.mount);
        }
        request = buildDownloadZipRequest(items, routes);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Selected items cannot be downloaded.';
        return Promise.reject(fileManagerError(message, items[0]!, FILE_MANAGER_ERROR.Other));
      }

      return downloadItemsAsZip(request.mountId, request.sources, request.archiveName)
        .then(() => undefined)
        .catch((error) => {
          const message = error instanceof Error ? error.message : 'Download failed.';
          return Promise.reject(fileManagerError(message, items[0]!, FILE_MANAGER_ERROR.Other));
        });
    }
  });
}

export function assertMountAvailable(mount: MountRoute): void {
  if (isMountUnavailable(mount)) {
    throw new Error(mountHealthMessage(mount) ?? `"${mount.key}" is unavailable.`);
  }
}

function parseParentDirectory(directory: FileSystemItem, routes: readonly MountRoute[]): ParsedVirtualPath | null {
  const virtual = ((directory as { path?: string })?.path ?? '').replace(/\\/g, '/').replace(/^\/+/, '');
  if (virtual.length === 0) return null;
  return parseVirtualPath(virtual, routes);
}

function getItemPath(item: { path?: string; key?: string; name: string; dataItem?: unknown }): string {
  const dataItem = item.dataItem as { __path?: string; path?: string; key?: string } | undefined;
  return dataItem?.__path ?? dataItem?.path ?? dataItem?.key ?? item.path ?? item.key ?? item.name;
}

async function transferItem(
  item: FileSystemItem,
  destinationDirectory: FileSystemItem,
  routes: readonly MountRoute[],
  operation: 'Copy' | 'Move'
): Promise<void> {
  return performFileTransfer(item, destinationDirectory, routes, operation);
}

async function checkOk(res: Response, item: FileSystemItem | undefined, conflictCode: number): Promise<void> {
  if (res.ok) return;
  await rejectFromResponse(res, item, conflictCode);
}

async function rejectFromResponse(
  res: Response,
  item: FileSystemItem | undefined,
  conflictCode: number
): Promise<never> {
  const message = await readErrorMessage(res);
  throw fileManagerError(message, item, errorCodeFromStatus(res.status, conflictCode));
}

function fileManagerError(message: string, item?: FileSystemItem, code: number = FILE_MANAGER_ERROR.Other): FileSystemError {
  return new FileSystemError(code, item, message);
}

function errorCodeFromStatus(status: number, conflictCode: number): number {
  if (status === 401 || status === 403) return FILE_MANAGER_ERROR.NoAccess;
  if (status === 404) return FILE_MANAGER_ERROR.FileNotFound;
  if (status === 409) return conflictCode;
  return FILE_MANAGER_ERROR.Other;
}

export function extractError(body: string, status: number): string {
  try {
    const data = JSON.parse(body) as Record<string, unknown>;
    if (typeof data.errorText === 'string') return data.errorText;
    if (typeof data.error === 'string') return data.error;
  } catch {
    // ignore parse errors
  }
  return body || `Request failed (${status}).`;
}
